import { ForwardedRef, forwardRef } from 'react';
import classNames from 'classnames';

import { DawnTriangleAlert } from '@xemplo/icons';
import { MaybeErrorOrDescription } from '@xemplo/input-utils';
import { useForwardedRef } from '@xemplo/ref-utils';

import { useInputFieldMetadata } from './use-input-field-metadata';
import { useValueTracker } from './use-value-tracker';
import * as S from './xemplo-input.styles';
import { XemploInputProps } from './xemplo-input.types';

/**
 * XemploInput is a customizable input component that supports various input types,
 * sizes, and additional features like leading and trailing icons, error handling,
 * and descriptions.
 *
 * @param props - The properties for the XemploInput component.
 * @returns A styled input element with optional label, icons, and error handling.
 */
export const XemploInput = forwardRef(function (
  props: XemploInputProps,
  forwardedRef?: ForwardedRef<HTMLInputElement>,
) {
  const {
    className,
    disabled,
    hasError,
    inputId,
    inputSize,
    isHiddenLabel,
    label,
    leadingIcon,
    readOnly,
    trailingIcon,
    width,
    wrapperId,
    inputProps,
    maybeErrorOrDescriptionProps,
    containerOnClick,
    showErrorIcon,
  } = useInputFieldMetadata(props);

  const { hasValue, handleChange } = useValueTracker(props);
  const inputRef = useForwardedRef({ forwardedRef });

  // If the input type is hidden, we return a simple input element
  // without any additional styling or label.
  if (props.type === 'hidden') return <input {...props} id={inputId} />;

  return (
    <S.Wrapper id={wrapperId} style={{ ...width, ...props.style }}>
      <S.InputContainer
        ref={props.wrapperRef}
        onClick={(e) => {
          if (containerOnClick) {
            containerOnClick(e);
          } else {
            inputRef.current?.focus();
          }
        }}
        className={classNames('xemplo-input-container', inputSize, className, {
          disabled,
          readOnly,
          invalid: hasError,
        })}
      >
        {leadingIcon}
        <S.Input
          {...inputProps}
          ref={inputRef}
          onChange={handleChange}
          className={classNames(inputSize, {
            'has-value': hasValue,
            'has-placeholder': !!props.placeholder,
            'has-label': !isHiddenLabel,
            'has-leading-icon': !!leadingIcon,
            'has-trailing-icon': !!trailingIcon,
            'has-error-icon': showErrorIcon,
            'label-hidden': isHiddenLabel,
          })}
        />
        <S.Label
          htmlFor={inputId}
          className={classNames(inputSize, {
            hidden: isHiddenLabel,
            left: !!leadingIcon,
          })}
          data-testid={`${inputId}-label`}
        >
          <S.LabelText>{label}</S.LabelText>
          {!isHiddenLabel && !props.required && <S.Optional>(optional)</S.Optional>}
        </S.Label>
        {showErrorIcon && <DawnTriangleAlert className="error" />}
        {trailingIcon}
      </S.InputContainer>
      <MaybeErrorOrDescription {...maybeErrorOrDescriptionProps} />
    </S.Wrapper>
  );
});

export default XemploInput;
